import React from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import styled from 'styled-components'
import { Callout } from 'sutra/components/Callout/Callout'

import { useRegisterBootstrapLoading } from 'components'
import { AvailableProjectRoot } from 'features/project/components/project-root/AvailableProjectRoot'
import { useProjectCacheAvailability } from 'features/project/components/project-root/hooks/use-project-cache-availability'
import { useProjectStoreSynced } from 'features/project/components/project-root/hooks/use-project-store-synced'
import { UnavailableProject } from 'features/project/components/project-root/UnavailableProject'
import { useFeatureFlags } from 'features/project/hooks/use-feature-flags'
import { useProjectDetails } from 'features/project/hooks/use-project-details'
import { useProjectList } from 'features/project/hooks/use-project-list'
import { ProjectLayout } from 'features/project/partials/project-layout/ProjectLayout'
import type { ProjectService } from 'features/project/service'
import { isInitialFlow } from 'features/project/utils/project-status'

type Props = {
  projectId: string
  projectService: ProjectService
}

export const ProjectRoot: React.FC<Props> = React.memo(function ProjectRoot({
  projectId,
  projectService,
}) {
  const location = useLocation()
  const settingsPath = `/project/${projectId}/settings`
  const defaultSettingsPath = `${settingsPath}/hub-settings`
  const isSettingsRoute =
    location.pathname === settingsPath ||
    location.pathname.startsWith(`${settingsPath}/`)

  // Single bootstrap gate for the whole project subtree: fire every
  // render-blocking query here, compute readiness once, and render exactly one
  // loader until everything resolves. All of these queries share the
  // react-query cache with their downstream consumers (AvailableProjectRoot,
  // ProjectLayout, ...), so this adds no extra network requests — it just moves
  // the loading decision to a single owner instead of a chain of per-component
  // loaders that each remount their own spinner.
  const cacheAvailability = useProjectCacheAvailability(
    projectService,
    projectId,
  )
  const { details, isError: projectDetailsIsError } =
    useProjectDetails(projectId)
  const { data: projectsData } = useProjectList()
  const { data: featureFlags } = useFeatureFlags()

  // Read the store copy of details so readiness also waits for
  // useProjectDetails' setDetails effect, and confirms the store reflects THIS
  // project (not a stale one from a previously-viewed hub).
  const storeSynced = useProjectStoreSynced(projectId)

  const projectInInitialFlow = details ? isInitialFlow(details.state) : false
  // The settings-redirect path is reserved for the "still processing"
  // (initial-flow) case; `available === false` and query errors now route to
  // <UnavailableProject /> instead (see PROD-3476).
  const isProjectLocked = projectInInitialFlow

  // Distinct "unavailable" terminal state: either the cache reports no data
  // available, or one of the two upstream queries failed. Only fires off the
  // settings subtree — settings keeps its existing in-route loading UX.
  // Initial-flow projects are excluded: their CH cache is not built yet, so
  // `available === false` (and transient cache errors during that window) are
  // expected steady state, not a real "unavailable" signal. They fall through
  // to the `isProjectLocked` branch below, which redirects to settings so the
  // user can finalize setup (see PROD-3476). When `details` is undefined,
  // `projectInInitialFlow` is `false`, so the empty state still fires for the
  // pre-details error window.
  const isProjectUnavailable =
    !isSettingsRoute &&
    !projectInInitialFlow &&
    (cacheAvailability.isError ||
      cacheAvailability.data?.available === false ||
      projectDetailsIsError)

  // Required network results before the first full render.
  const inputsReady = !!details && !!projectsData

  const isReady = isSettingsRoute
    ? storeSynced && !!featureFlags
    : isProjectUnavailable ||
      (!cacheAvailability.isLoading &&
        inputsReady &&
        storeSynced &&
        !!featureFlags)

  // Register into the global overlay; must be called before any early return.
  useRegisterBootstrapLoading('project', !isReady)

  if (!isReady) return null

  if (isProjectUnavailable) return <UnavailableProject />

  if (isSettingsRoute) {
    return (
      <SettingsProjectRoot
        isProjectLocked={isProjectLocked}
        projectId={projectId}
      />
    )
  }

  if (isProjectLocked) {
    return (
      <LockedProjectLayout projectId={projectId}>
        <Navigate to={defaultSettingsPath} replace />
      </LockedProjectLayout>
    )
  }

  return <AvailableProjectRoot projectId={projectId} />
})

type SettingsProjectRootProps = {
  isProjectLocked: boolean
  projectId: string
}

const SettingsProjectRoot: React.FC<SettingsProjectRootProps> = ({
  isProjectLocked,
  projectId,
}) => (
  <LockedProjectLayout projectId={projectId} isProjectLocked={isProjectLocked}>
    {isProjectLocked ? (
      <LockedProjectBanner
        variant="warning"
        role="alert"
        title="Project setup is still in progress"
        description="Until this project finishes building, only Settings is available. You can update setup details here; the rest of the workspace will unlock automatically when the project is ready."
      />
    ) : null}
    <SettingsContent>
      <Outlet />
    </SettingsContent>
  </LockedProjectLayout>
)

type LockedProjectLayoutProps = {
  children: React.ReactNode
  projectId: string
  isProjectLocked?: boolean
}

const LockedProjectLayout: React.FC<LockedProjectLayoutProps> = ({
  children,
  projectId,
  isProjectLocked = false,
}) => (
  <ProjectLayout isProjectLocked={isProjectLocked}>
    <SettingsRoot key={projectId}>{children}</SettingsRoot>
  </ProjectLayout>
)

const SettingsRoot = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

const LockedProjectBanner = styled(Callout)`
  flex-shrink: 0;
  margin: 16px 24px 0;
`

const SettingsContent = styled.div`
  flex: 1;
  min-height: 0;
`
